import React, { createContext, useContext, useEffect } from 'react'
import * as Sentry from '@sentry/browser'
import moment from 'moment'

import {
  calculateAvailabilities,
  confirmReserve,
  getReserve,
  insertReserve,
  insertScheduling,
  reschedule
} from '../services/schedulingService'

import { getPrice } from '../services/priceService'
import { getPetsLocally, batchCreateOrEditPet, clearPetsLocally } from '../services/petProfileService'
import { getAllMyPets } from '../services/petService'
import { getServices } from '../services/serviceService'
import { getNames } from '../services/professionalService'
import { getPartnerById } from '../services/partnerService'

import { getCampaignsByPartnerAndService } from '../services/campaignService'

import {
  scrollTop,
  calcDiscountedPrice,
  PAGE_URL,
  splitServicesList,
  calculateTotalPrice
} from '../utils'
import MODAL from '../utils/modals'
import * as gtag from '../utils/gtag'
import {
  DEFAULT_ERROR,
  INITIAL_ACTIVE_STEP,
  INITIAL_CALENDAR_DATE,
  INITIAL_SCHEDULE_BODY,
  INITIAL_SCHEDULING_STATE,
  SCHEDULING_ERROR_MESSAGES
} from '../utils/schedulingConstants'

import { PetAndServicesContextProvider } from './petAndServicesContext'
import { ProfessionalContextProvider } from './professionalContext'
import { LocationContextProvider } from './locationContext'
import { DateTimeContextProvider } from './dateTimeContext'
import { getActiveCoupon } from 'src/services/couponService'
import useReducerWithCallback from './useReducerWithCallback'
import { usePaymentContext } from './paymentContext'
import { getValidCoupon } from 'src/services/schedulingCouponService'
import { getBestPromotion } from 'src/services/promotionService'

const serviceScheduleContext = createContext()

// funções
export const actionTypes = {
  UPDATE_STATE: 'UPDATE_STATE',
  RESET_STATE: 'RESET_STATE'
}

// atualiza o estado
const reducer = (state, action) => {
  switch (action.type) {
    case actionTypes.UPDATE_STATE:
      return { ...state, ...action.payload }
    case actionTypes.RESET_STATE:
      return INITIAL_SCHEDULING_STATE
    default:
      return state
  }
}

// salva o estado no localStorage
const saveStateToLocalStorage = (state) => {
  try {
    const serializedState = JSON.stringify(state)
    window.localStorage.setItem('schedulingState', serializedState) // eslint-disable-line
  } catch (err) {
    console.error('Erro ao salvar no localStorage:', err)
  }
}

// carrega o estado do localStorage
const loadStateFromLocalStorage = () => {
  try {
    const serializedState = window.localStorage.getItem('schedulingState') // eslint-disable-line
    return serializedState ? JSON.parse(serializedState) : undefined
  } catch (err) {
    console.error('Erro ao carregar do localStorage:', err)
    return undefined
  }
}

// contexto de estado
export function ServiceScheduleContextProvider ({ children }) {
  const initialState = loadStateFromLocalStorage() || INITIAL_SCHEDULING_STATE
  const { functions: { updateSchedulings } } = usePaymentContext()
  const [state, dispatch] = useReducerWithCallback(reducer, initialState)
  const handleShouldInvertSteps = () => {
    const { scheduleBody, formList, petsData } = state

    const selectedPet = petsData.find(pet => pet.id === scheduleBody.pet_id)
    const prevSchedule = formList[formList.length - 1]
    const stateToRender = !selectedPet && prevSchedule ? prevSchedule.data : state

    return stateToRender.scheduleBody.samePetDate || stateToRender.invertStep
  }

  const isAdditionalServicesEqual = (prevScheduling, currentScheduling) => {
    const prevServices = prevScheduling.additionalServices?.map(({ id }) => id).sort() || []
    const currentServices = currentScheduling.additionalServices?.map(({ id }) => id).sort() || []

    return (
      prevServices.length === currentServices.length &&
      prevServices.every((id, index) => id === currentServices[index])
    )
  }

  const hasSchedulingChanged = (updatedState = false, ignoreDate = false) => {
    const { scheduleBody, schedulingData } = updatedState || state
    const additionalServicesEqual = isAdditionalServicesEqual(schedulingData, scheduleBody)

    return (
      schedulingData &&
      (
        scheduleBody.pet_id !== schedulingData.pet?.id ||
        scheduleBody.service_id !== schedulingData.service?.id ||
        scheduleBody.partner_id !== schedulingData.partner?.id ||
        scheduleBody.professional_id !== schedulingData.professional?.id ||
        state?.schedulingCoupon?.id !== schedulingData?.schedulingCoupon?.id ||
        (!ignoreDate && (scheduleBody.selectDay !== schedulingData.selectDay || scheduleBody.selectTime !== schedulingData.selectTime)) ||
        !additionalServicesEqual
      )
    )
  }

  const schedulingItemErrorCallback = (error) => {
    dispatch({
      type: actionTypes.UPDATE_STATE,
      payload: {
        schedulingItemError: error
      }
    })
  }

  // verifica se a data já está registrada para outro profissional no caso de multiplos agendamentos
  const checkReservedDate = ({ professional, selectDay, selectTime }) => {
    const { formList, pricesData } = state
    // Dia selecionado + horário de início do serviço em agendamento
    const selectedStartDate = moment(selectDay + ' ' + selectTime, 'YYYY-MM-DD HH:mm')
    // Dia selecionado + horário de término do serviço em agendamento
    const selectedEndDate = selectedStartDate.clone().add(pricesData?.[0]?.service_time || 60, 'minutes')

    return !formList.some(form => {
      // Data de início do serviço agendado
      const serviceStart = moment(form.body.selectDay + ' ' + form.body.selectTime, 'YYYY-MM-DD HH:mm')
      const mainService = form.data.pricesData.find((price) => price.additional === false)
      const serviceEnd = serviceStart.clone().add(mainService.service_time, 'minutes')
      // Se a data e hora selecionada está entre o início e fim de um serviço já agendado, significa que ela está indisponível e não deve ser liberada para seleção no calendário
      const isUnavailable = selectedStartDate.isBetween(serviceStart, serviceEnd, null, '[)') || selectedEndDate.isBetween(serviceStart, serviceEnd, null, '(]')

      // Se a data/hora está ocupada para o profissional selecionado
      return isUnavailable && form.body.professional_id === professional
    })
  }

  // atualiza quando lojas terminaram de carregar
  const handleSetRescheduleDataLoaded = () => {
    if (!state.rescheduleDataLoaded) {
      dispatch({ type: actionTypes.UPDATE_STATE, payload: { rescheduleDataLoaded: true } })
    }
  }

  // fecha modal de erro
  const handleWarningModalClose = () => {
    dispatch({ type: actionTypes.UPDATE_STATE, payload: { modal: undefined } })
  }

  // retorna modal de erro
  const showSchedulingFailureModal = (partnerId) => {
    const {
      partnersData,
      scheduleBody
    } = state

    const { phone } = {
      ...partnersData.find(({ id }) => id === partnerId)
    }

    return {
      modal: MODAL.SCHEDULE_ERROR_GENERIC(handleWarningModalClose(), phone),
      scheduleBody: {
        ...scheduleBody
      },
      pricesData: []
    }
  }

  const showCouponLimitReachedModal = (router, errorMessage) => {
    const { activeStep } = state
    const onConfirm = () => {
      handleWarningModalClose()
      router.push({
        pathname: `${PAGE_URL.SCHEDULING_REVIEW}`,
        query: { ...router.query, showDiary: false }
      })
    }

    dispatch({
      type: actionTypes.UPDATE_STATE,
      payload: {
        modal: MODAL.COUPON_LIMIT_REACHED_ERROR(
          onConfirm,
          errorMessage
        ),
        isHowToPay: false,
        activeStep: (activeStep - 1),
        review: true
      }
    })
  }

  // retorna campanhas por mês
  const handleFetchCampaigns = async (calendarDate, partnerId, serviceId) => {
    const campaignList = await getCampaignsByPartnerAndService(partnerId, serviceId, calendarDate)

    return campaignList
  }

  const addCouponToSchedule = async ({ context, coupon, callback }) => {
    context.showLoader()

    const { pricesData, activeCampaign, formList } = state
    const price = calculateTotalPrice(pricesData)

    const params = {
      code: coupon,
      purchaseValue: price,
      user: window.localStorage.getItem('userId'),
      partnerId: state.scheduleBody.partner_id,
      serviceId: state.scheduleBody.service_id,
      date: state.scheduleBody.selectDay
    }

    const schedulingCoupon = await getValidCoupon(params)

    if (schedulingCoupon?.success) {
      if (activeCampaign) {
        const bestDiscount = await getBestPromotion(activeCampaign.id, schedulingCoupon.data.id, price)

        dispatch({
          type: actionTypes.UPDATE_STATE,
          payload: {
            bestDiscount
          }
        })

        if (bestDiscount.promotion === 'campaign') {
          callback()
          return context.hideLoader()
        }
      }

      if (formList.length) {
        const couponId = schedulingCoupon.data.id
        let isCouponAlreadyUsed = false
        let couponIsLimitReached = false

        formList.some(({ scheduling }) => {
          const coupon = scheduling?.schedulingCoupon
          if (!coupon || coupon.id !== couponId) return false

          if (coupon.uniquePerClient) {
            isCouponAlreadyUsed = true
            return true
          }

          const maxUsage = coupon.maxUsage
          const usageCount = coupon.usageCount + 1
          if (usageCount >= maxUsage) {
            couponIsLimitReached = true
            return true
          }

          return false
        })

        if (isCouponAlreadyUsed) {
          // eslint-disable-next-line standard/no-callback-literal
          callback(['Cupom já utilizado'])
          context.hideLoader()
          return
        }

        if (couponIsLimitReached) {
          // eslint-disable-next-line standard/no-callback-literal
          callback(['Cupom esgotado'])
          context.hideLoader()
          return
        }
      }

      dispatch({
        type: actionTypes.UPDATE_STATE,
        payload: {
          schedulingCoupon: schedulingCoupon.data,
          bestDiscount: undefined
        }
      })
      callback()
      context.hideLoader()
      return
    }
    callback(schedulingCoupon.errors)
    context.hideLoader()
  }

  const removeCouponFromSchedule = () => {
    const { campaigns, scheduleBody: { selectDay, partner_id: partnerId, service_id: serviceId } } = state

    const activeCampaign = campaigns?.find((campaign) => (moment(selectDay).isBetween(
      moment(campaign.start),
      moment(campaign.end),
      null,
      '[]'
    )) && campaign.status === 'ACTIVE' &&
      campaign.partners.includes(partnerId) && campaign.services.includes(serviceId))

    dispatch({
      type: actionTypes.UPDATE_STATE,
      payload: {
        activeCampaign: activeCampaign,
        schedulingCoupon: undefined
      }
    })
  }

  const handleFetchCoupon = async () => await getActiveCoupon()

  const handleEditPetScheduling = (selectedForm, step = 1) => {
    const { formList, havePayment } = state
    const { body, data } = selectedForm
    const updatedFormList = formList.filter(
      (form) => form.body.pet_id !== body.pet_id
    )
    const updatedProfessionalData = data.professionalData.filter(
      ({ enabledPayment }) => (havePayment && enabledPayment === havePayment) || !havePayment || formList.length === 1
    )

    dispatch({
      type: actionTypes.UPDATE_STATE,
      payload: {
        ...data,
        professionalData: updatedProfessionalData,
        formList: updatedFormList,
        review: true,
        isHowToPay: false,
        activeStep: step,
        schedulingItemError: undefined
      }
    })
    scrollTop('auto')
  }

  // fecha modal de agendamento duplicado
  const handleCloseWarningDuplicatedScheduling = (router, schedulingIndexError) => {
    const schedulings = [...state.formList.map(formList => formList), state.scheduleBody]
    const schedulingLength = schedulings.length - 1

    if (schedulingLength !== schedulingIndexError) {
      const schedulingToEdit = schedulings[schedulingIndexError]
      return handleEditMultipleScheduling(schedulingToEdit)
    }

    const stepToRedirect = state.invertStep ? 4 : 5

    dispatch({
      type: actionTypes.UPDATE_STATE,
      payload: {
        ...editSteps[stepToRedirect],
        modal: undefined,
        review: false,
        isHowToPay: false,
        scheduleBody: {
          ...editSteps[stepToRedirect].scheduleBody,
          selectDay: undefined,
          selectTime: undefined
        }
      }
    })

    const { path } = steps.find(item => item.id === stepToRedirect)

    const { category } = router.query

    if (!category) {
      router.push(`/agendamentos${path}`)
      return
    }

    router.push(`/agendamentos${path}/?category=${category}`)
  }

  // exibe modal de agendamento duplicado
  const showSchedulingDuplicateModal = ({ errorMessage, callback, router, schedulingIndexError }) => {
    dispatch({
      type: actionTypes.UPDATE_STATE,
      payload: {
        modal: MODAL.DUPLICATED_SCHEDULING_WARNING(() => handleCloseWarningDuplicatedScheduling(router, schedulingIndexError), errorMessage)
      }
    })

    if (callback) callback()
  }

  const handleScheduleEventSend = async ({ scheduling, isReactNative }) => {
    try {
      const { serviceData } = state
      const schedulingsCreated = [scheduling, ...scheduling.nestedSchedulings || []]

      const promises = schedulingsCreated.map(async schedulingCreated => {
        const serviceCategory = serviceData.find(service => service.value === schedulingCreated.service.id)?.category

        const purchaseDetails = {
          scheduling: schedulingCreated,
          serviceCategory,
          isReactNative: isReactNative
        }

        gtag.scheduleEvent(purchaseDetails)
      })

      await Promise.all(promises)
    } catch (err) {
      Sentry.captureException(err)
    }
  }

  // busca disponibilidade de profissionais por loja, também busca dias disponiveis, horas e campanhas
  const findPartnerScheduleAndCampaignAviability = async (partner, day = undefined, selectedForm = false) => {
    const {
      scheduleBody,
      error,
      calendarMonth,
      calendarYear,
      partnersData,
      professionalData,
      schedulingData,
      formList,
      timeData,
      selectedPet,
      petsData
    } = state

    const {
      breed: { id: breedId },
      temperament
    } = selectedPet

    const {
      service_id: serviceId,
      professional_id: professionalId,
      additional_services: additionalServices
    } = scheduleBody

    const newError = Object.assign({}, error)

    // usa dados de multiplo agendamento para selecionar dia e hora
    const partnerId = selectedForm ? selectedForm.body.partner_id : partner
    const selectDay = selectedForm ? selectedForm.body.selectDay : day
    const selectTime = selectedForm ? selectedForm.body.selectTime : undefined
    const partnerDataList = selectedForm ? selectedForm.data.partnersData : partnersData
    const selectedPetSchedule = selectedForm
      ? {
        id: selectedForm.body.pet_id,
        name: selectedForm.scheduling.petName
      }
      : undefined

    newError.partner_id = partnerId ? undefined : DEFAULT_ERROR.partner_id

    let newState = {
      ...state,
      availableDates: [],
      timeData: timeData || [],
      error: {
        ...error,
        partner_id: partnerId ? undefined : DEFAULT_ERROR.partner_id,
        ...(selectedForm && {
          selectDay: selectDay ? undefined : DEFAULT_ERROR.selectDay,
          selectTime: selectTime
            ? undefined
            : DEFAULT_ERROR.selectTime
        })
      },
      // Se já foi adicionado um agendamento para um pet,
      // define se haverá pagamento ou não de acordo com o primeiro agendamento
      havePayment: formList.length ? formList[0].data.havePayment : false
    }

    try {
      const partnerSelected = partnersData.find(
        (partner) => partner.id === partnerId
      )

      const partnerEnablePayment = partnerSelected?.enabledPayment === true
      const partnerOpenSeller = partnerSelected?.openSeller === true

      const professionalSelected = professionalData.find(
        (professional) => professional.id === professionalId
      )
      const professionalEnablePayment = professionalSelected?.enabledPayment === true

      const unchanged = !hasSchedulingChanged(state) || professionalId === undefined

      const havePrevPayment = schedulingData.paidAt !== undefined && unchanged

      // Se já foi adicionado um agendamento para um pet, define se haverá pagamento ou não de acordo com o primeiro agendamento,
      // caso contrário, verifica se o pagamento está habilitado para a loja e profissional selecionados no agendamento atual
      const havePayment = formList.length
        ? formList[0].data.havePayment
        : partnerEnablePayment &&
        (professionalEnablePayment || partnerOpenSeller)

      newState.havePayment = havePayment

      // busca slots disponiveis (se a loja possui profissionais disponíveis)
      const availableSlots = await calculateAvailabilities({
        partnerId,
        serviceId,
        additionalServices,
        breedId,
        temperament,
        month: (selectDay) // Se é um agendamento para mais de 1 pet e foi selecionado a opção de mesma data/hora já agendado para um dos pets
          ? moment(selectDay, 'YYYY-MM-DD').month() + 1 // Seta o mês relativo à data já selecionada
          : calendarMonth,
        year: (selectDay) // Se é um agendamento para mais de 1 pet e foi selecionado a opção de mesma data/hora já agendado para um dos pets
          ? moment(selectDay, 'YYYY-MM-DD').year() // Seta o ano relativo à data já selecionada
          : calendarYear,
        ...(havePrevPayment && {
          paidAt: schedulingData.paidAt
        }),
        ...(havePayment && { havePayment })
      })

      newState.availableSlots = availableSlots

      if (availableSlots) {
        // busca profissionais caso queira repetir a data e hora de um agendamento
        if (selectedForm) {
          let professionalIds = Object.keys(availableSlots).filter(
            (professionalId) => professionalId !== ''
          )

          // Se o steps de data e hora vem primeiro do que o step de profissional (por opção ou por ser mesmo dia e horário de um agendamento anterior),
          // filtra o array de profissionais para exibir somente os que atendem data/hora já selecionada

          if (selectDay) {
            const day = moment(selectDay, 'YYYY-MM-DD').date()
            professionalIds = professionalIds.filter(
              (professional) =>
                availableSlots[professional][day] &&
                availableSlots[professional][day].includes(selectTime)
            )

            // Se existem agendamentos anteriores, filtra os profissionais que já foram selecionados no mesmo dia e horário escolhido no agendamento atual
            if (formList.length > 0) {
              professionalIds = professionalIds.filter((professional) => formList.every(
                (form) => (selectDay === form.body.selectDay && selectTime === form.body.selectTime) ? form.body.professional_id !== professional : true))
            }
          }

          const professionalList = await getNames(professionalIds)

          const professionalData = professionalList
            .map(({ id, name, photo, enabledPayment, ratingAverage }) => ({ id, name, photo, enabledPayment: enabledPayment || false, ratingAverage }))
            .filter(({ enabledPayment }) => !formList.length || (havePayment && enabledPayment === havePayment) || !havePayment)

          if (!professionalData.length) {
            const unavailablePetSchedule = petsData.find(
              (pet) => selectedPetSchedule && pet.id === selectedPetSchedule.id
            )

            if (unavailablePetSchedule) {
              const newError = {
                ...error,
                unavailabilityPets: [
                  ...error.unavailabilityPets,
                  {
                    ...unavailablePetSchedule
                  }
                ]
              }
              newState.error = newError
            }
          }

          newState.professionalData = professionalData
        }

        newState = {
          ...newState,
          scheduleBody: {
            ...newState.scheduleBody,
            partner_id: partnerId,
            ...(selectedForm && {
              selectDay,
              selectTime
            })
          },
          ...(selectedForm && {
            selectedPetSchedule,
            partnersData: partnerDataList,
            invertStep: true
          })
        }

        // se tem profissionais disponíveis atualiza preços do serviço
        const { timeData, scheduleBody } = newState

        const professionalId = scheduleBody.professional_id || ''

        // Inicialização dos arrays de dias disponíveis
        const availableDates = availableSlots[professionalId]
          ? Object.keys(availableSlots[professionalId]).map((day) => parseInt(day))
          : []

        const calendarDate = {
          month: selectDay // Se é um agendamento para mais de 1 pet e foi selecionado a opção de mesma data/hora já agendado para um dos pets
            ? moment(selectDay, 'YYYY-MM-DD').month() + 1 // Seta o mês relativo à data já selecionada
            : calendarMonth,
          year: selectDay // Se é um agendamento para mais de 1 pet e foi selecionado a opção de mesma data/hora já agendado para um dos pets
            ? moment(selectDay, 'YYYY-MM-DD').year() // Seta o ano relativo à data já selecionada
            : calendarYear
        }

        const services = [...additionalServices, serviceId]

        const [campaignList, price] = await Promise.all([
          // busca por campanhas disponíveis no mês
          handleFetchCampaigns(
            calendarDate,
            partnerId,
            serviceId
          ),
          // busca pelo preço dos serviços
          getPrice({
            partnerId,
            breedId,
            services
          })
        ])

        // seta os dias disponiveis e as campanhas
        newState = {
          ...newState,
          availableDates,
          timeData: timeData || [],
          campaigns: campaignList
        }

        // caso tenha o dia selecionado já seta campanha ativa
        if (selectDay) {
          const activeCampaign = campaignList?.find((campaign) => (moment(selectDay).isBetween(
            moment(campaign.start),
            moment(campaign.end),
            null,
            '[]'
          )) && campaign.status === 'ACTIVE' &&
            campaign.partners.includes(partnerId) && campaign.services.includes(serviceId))

          newState.activeCampaign = activeCampaign
        }

        if (!price.length) {
          throw new Error('Nenhum valor foi encontrado para esse serviço.')
        }

        const pricesData = price
          ? price.map((priceData) => {
            return {
              service: priceData.service.name,
              price: priceData.price_value,
              service_time: priceData.service_time,
              additional: priceData.service.additional
            }
          })
          : []

        // seta o preço dos serviços
        newState.pricesData = pricesData
      } else {
        newState = {
          ...newState,
          ...showSchedulingFailureModal(partner)
        }

        handleSetRescheduleDataLoaded()
      }

      return newState
    } catch (err) {
      console.error(err)

      newState = {
        ...newState,
        ...showSchedulingFailureModal(partner)
      }

      handleSetRescheduleDataLoaded()

      return newState
    }
  }

  // adiciona mais um pet ao agendamento
  const handleAddPetToScheduling = (discountedPrice, onGoTo) => {
    const {
      scheduleBody,
      professionalData,
      serviceData,
      additionalServiceData,
      petsData,
      partnersData,
      pricesData,
      schedulingCoupon
    } = state

    const schedulingPrice = pricesData.reduce((prev, next) => {
      return Number(prev) + Number(next.price)
    }, 0)

    const additionalServicesNames = scheduleBody.additional_services.map(
      (service) =>
        additionalServiceData.find(
          (additionalServiceItem) => additionalServiceItem.value === service
        ).name
    )

    dispatch({
      type: actionTypes.UPDATE_STATE,
      payload: {
        formList: [
          ...state.formList,
          {
            body: {
              ...scheduleBody
            },
            services: pricesData.map((service) => {
              return {
                price: service.price,
                professionalName: professionalData.find(
                  (professional) =>
                    professional.id === scheduleBody.professional_id
                )?.name,
                partnerName: partnersData.find(
                  (partner) => partner.id === scheduleBody.partner_id
                )?.name,
                serviceName: service.service,
                additional: service.additional,
                petName: petsData.find((pet) => pet.id === scheduleBody.pet_id)
                  ?.name
              }
            }),
            scheduling: {
              price: schedulingPrice,
              discountedPrice,
              schedulingCoupon,
              professionalName: professionalData.find(
                (professional) => professional.id === scheduleBody.professional_id
              )?.name,
              partnerName: partnersData.find(
                (partner) => partner.id === scheduleBody.partner_id
              )?.name,
              serviceName: serviceData.find(
                (service) => service.value === scheduleBody.service_id
              )?.name,
              additionalServices: additionalServicesNames,
              petName: petsData.find((pet) => pet.id === scheduleBody.pet_id)
                ?.name
            },
            data: {
              ...state
            }
          }
        ],
        scheduleBody: INITIAL_SCHEDULE_BODY,
        professionalData: [],
        pricesData: [],
        timeData: [],
        availableDates: [],
        error: {
          ...DEFAULT_ERROR
        },
        haveError: false,
        usingGeolocation: false,
        modal: undefined,
        professionalProfile: undefined,
        calendarMonth: INITIAL_CALENDAR_DATE.calendarMonth,
        calendarYear: INITIAL_CALENDAR_DATE.calendarYear,
        obs: '',
        selectedPetSchedule: undefined,
        isAllAdditionalServicesSelected: false,
        review: false,
        isHowToPay: false,
        activeStep: INITIAL_ACTIVE_STEP,
        noPreference: false,
        changeStore: false,
        activeCampaign: { discountType: 'PERCENTAGE', discountValue: '5' },
        invertStep: undefined,
        selectedPet: {},
        schedulingCoupon: undefined
      }
    }, () => {
      onGoTo(steps[0].path)
    })

    scrollTop('auto')
  }

  const handleEditMultipleScheduling = (form) => {
    dispatch({
      type: actionTypes.UPDATE_STATE,
      payload: {
        modal: MODAL
          .EDIT_MULTIPLE_SCHEDULING({
            onCancel: () => handleWarningModalClose(),
            onConfirm: () => {
              handleEditPetScheduling(form)
              handleWarningModalClose()
            }
          })
      }
    })
  }

  // finaliza agendamento
  const handleConfirmScheduling = async ({ reschedule, context, router }) => {
    const { error, formList } = state

    if (
      (error.pet_id ||
        error.service_id ||
        error.professional_id ||
        error.selectDay ||
        error.selectTime) &&
      !formList.length
    ) {
      dispatch({
        type: actionTypes.UPDATE_STATE,
        payload: {
          haveError: true
        }
      })
    } else {
      handleInsertSchedulingByPaymentType({ context, router })
    }
  }

  const handleInsertSchedulingByPaymentType = async ({ context, router }) => {
    handleWarningModalClose()

    const redirectToCheckout = (schedulings) => {
      const { nestedSchedulings, ...rest } = schedulings
      // em casos de reagendamento não existe nestedSchedulings
      const flattenedSchedulings = Array.isArray(nestedSchedulings)
        ? nestedSchedulings.map(scheduling => scheduling)
        : []

      const schedulingList = [{ ...rest }, ...flattenedSchedulings]
      updateSchedulings(schedulingList)

      router.push(`${PAGE_URL.CHECKOUT_ADDRESS}`)
    }

    const {
      scheduleBody,
      formList,
      obs,
      petsData,
      schedulingCoupon
    } = state

    const isReschedule = router.query.id && router.query.reschedule
    const { paymentType } = scheduleBody
    const isPaymentOnline = paymentType === 'online'

    try {
      context.showLoader()
      const allSchedulings = formList.map((form) => {
        const requestedPet = context.firstName
          ? { id: form.body.pet_id }
          : {
            ...petsData[form.body.pet_id],
            id: undefined,
            breed: petsData[form.body.pet_id].breed.id
          }
        return {
          partnerId: form.body.partner_id,
          serviceId: form.body.service_id,
          additionalServices: form.body.additional_services,
          professionalId: form.body.professional_id,
          date: `${form.body.selectDay} ${form.body.selectTime}`,
          obs: form.data.obs,
          pet: requestedPet,
          ...(form.scheduling?.schedulingCoupon ? { couponId: form.scheduling.schedulingCoupon?.id } : {})
        }
      })

      let requestBody = {}
      if (
        (scheduleBody.pet_id || scheduleBody.pet_id === 0) &&
        scheduleBody.service_id &&
        scheduleBody.partner_id &&
        scheduleBody.professional_id &&
        scheduleBody.selectDay &&
        scheduleBody.selectTime
      ) {
        const date = scheduleBody.selectDay
          .concat(' ')
          .concat(scheduleBody.selectTime)
        const professionalId = scheduleBody.professional_id
        requestBody = {
          partnerId: scheduleBody.partner_id,
          serviceId: scheduleBody.service_id,
          additionalServices: scheduleBody.additional_services,
          couponId: schedulingCoupon?.id,
          professionalId,
          date,
          obs
        }

        context.firstName
          ? (requestBody.pet = {
            id: scheduleBody.pet_id
          })
          : (requestBody.pet = {
            ...petsData[scheduleBody.pet_id],
            id: undefined,
            breed: petsData[scheduleBody.pet_id].breed.id
          })

        allSchedulings.push(requestBody)
      }

      const multi = allSchedulings.length > 1 ? '?multi=true' : ''

      const isReactNative = context.isReactNative

      // INSERE AGENDAMENTO CASO USUÁRIO ESTEJA LOGADO
      if (context.firstName) {
        let scheduling = {}
        if (isReschedule) {
          scheduling = await reschedule(router.query.id, { ...requestBody, paymentType })
          await handleScheduleEventSend({ scheduling, isReactNative })
          // Verifica se o pet, serviço, loja ou profissional foram alterados no reagendamento e está liberado o pagamento para o seller
          if (hasSchedulingChanged() && isPaymentOnline) {
            // Se o agendamento gerou um pedido de pagamento, redireciona pra o checkout de pagamento
            redirectToCheckout(scheduling)
          } else {
            // Se o agendamento não gera um novo pagamento, redireciona para a home
            router.push(`${PAGE_URL.SCHEDULING_SUMMARY}/${scheduling.id}`)
          }
        } else if (isPaymentOnline) {
          scheduling = await insertScheduling(allSchedulings, true)
          await handleScheduleEventSend({ scheduling, isReactNative })
          redirectToCheckout(scheduling)
        } else {
          scheduling = await insertScheduling(allSchedulings)
          await handleScheduleEventSend({ scheduling, isReactNative })
          router.push(
            `${PAGE_URL.SCHEDULING_SUMMARY}/${scheduling.id}${multi}`
          )
        }
      // INSERE RESERVA PARA USUÁRIO NÃO LOGADO
      } else {
        if (!window.sessionStorage.getItem('reserve')) {
          await insertReserve(allSchedulings, isPaymentOnline)
        }
        const onAuthenticate = async () => {
          context.showLoader()
          try {
            const allPets = petsData.map((pet) => ({
              ...pet,
              id: undefined,
              breed: pet.breed.id
            })) // pets não envolvidos no agendamento
            const { id: reserveId } = getReserve()

            if (!allPets.length) {
              throw new Error('Nenhum pet selecionado')
            }

            const petsId = await batchCreateOrEditPet(allPets)

            const scheduling = await confirmReserve(petsId)
            await handleScheduleEventSend({ scheduling, isReactNative })
            clearPetsLocally()

            if (isPaymentOnline) {
              window.localStorage.setItem(
                'reservePay',
                JSON.stringify({
                  id: reserveId,
                  expiresAt: moment()
                    .add(process.env.SCHEDULING_TTL, 'minutes')
                    .format('YYYY-MM-DD HH:mm')
                })
              )
              redirectToCheckout(scheduling)
            } else {
              router.push(
                `${PAGE_URL.SCHEDULING_SUMMARY}/${reserveId}${multi}`
              )
            }
          } catch (err) {
            if (err.response && err.response.status === 422) {
              const callback = async () => {
                await context.logoutUser()
              }
              const errorMessage = err.response.data.message
              const schedulingIndexError = err.response.data.index
              showSchedulingDuplicateModal({
                errorMessage,
                callback,
                router,
                schedulingIndexError
              })
            } else {
              console.error(err)
              Sentry.captureException(err)
            }
          } finally {
            context.toggleModalSheet()
            context.hideLoader()
          }
        }
        context.toggleModalSheet(MODAL.SCHEDULE_RESERVED_LOGIN({ onCancel: context.toggleModalSheet, callback: onAuthenticate }))
      }
    } catch (err) {
      const errorData = err.response?.data
      const errorMessage = err.response?.data?.message
      const schedulingIndexError = err.response?.data?.index
      if (err.response.status === 422) {
        return showSchedulingDuplicateModal({
          errorMessage,
          callback: () => schedulingItemErrorCallback(errorData),
          router,
          schedulingIndexError
        })
      }

      if (SCHEDULING_ERROR_MESSAGES.coupon_limit_reached === errorMessage) {
        return showCouponLimitReachedModal(router, errorMessage)
      }

      dispatch({ type: actionTypes.UPDATE_STATE, payload: showSchedulingFailureModal(scheduleBody?.partner_id) })
    } finally {
      context.hideLoader()
    }
  }

  // informações de preço
  const calcTotalPrice = () => {
    const { pricesData, formList } = state
    const scheduledPrice = formList.reduce(
      (prev, next) => Number(prev) + Number(next.scheduling.price),
      0
    )
    const schedulingPrice = pricesData.reduce(
      (prev, next) => Number(prev) + Number(next.price),
      0
    )
    return scheduledPrice + schedulingPrice
  }

  const calcDiscountedTotal = (currentSchedulePrice, currentPriceWithDiscount) => {
    const { formList, activeCampaign, schedulingCoupon } = state

    const scheduledPrice = formList.reduce(
      (prev, next) => Number(prev) + Number(next.data.activeCampaign || next.data.schedulingCoupon ? next.scheduling.discountedPrice : next.scheduling.price),
      0
    )

    const schedulingPrice = activeCampaign || schedulingCoupon ? currentPriceWithDiscount : currentSchedulePrice || 0
    return scheduledPrice + schedulingPrice
  }

  const pricesInfo = () => {
    const { pricesData, activeCampaign, schedulingCoupon } = state
    const discount = schedulingCoupon ?? activeCampaign
    const currentSchedulePrice = calculateTotalPrice(pricesData)
    const currentPriceWithDiscount = calcDiscountedPrice(discount, currentSchedulePrice)
    const discountedTotal = calcDiscountedTotal(currentSchedulePrice, currentPriceWithDiscount)
    const fullPrice = calcTotalPrice()

    return {
      currentSchedulePrice,
      currentPriceWithDiscount,
      totalPriceWithDiscount: discountedTotal,
      fullPrice,
      currentHasDiscount: currentSchedulePrice !== currentPriceWithDiscount
    }
  }

  // remove o pet caso volte na tela inicial
  const handleUnselectPet = () => {
    const { scheduleBody } = state

    dispatch({
      type: actionTypes.UPDATE_STATE,
      payload: {
        scheduleBody: {
          ...scheduleBody,
          pet_id: undefined,
          professional_id: ''
        },
        selectedPet: undefined
      }
    })
  }

  // busca e seleciona agendamento externo (minha agenda)
  const handleEditExternalScheduling = async (props, schedulingInfo, withoutDate = false) => {
    const { context } = props
    const isEditing = schedulingInfo.status === 'SCHEDULED'

    let updatedState = INITIAL_SCHEDULING_STATE

    // step 1

    try {
      const petsList = (context.firstName && (await getAllMyPets())) || getPetsLocally()

      const selectedPet = petsList.filter(({ id }) => id === schedulingInfo?.pet?.id)

      if (!selectedPet.length) {
        throw new Error('Não foi possível encontrar o PET do seu agendamento.')
      }

      if (!schedulingInfo?.service?.category) {
        throw new Error('Não foi possível encontrar a categoria do seu agendamento.')
      }

      const serviceList = await getServices(schedulingInfo?.service?.category)

      const selectedService = serviceList.filter(({ id }) => id === schedulingInfo?.service?.id)
      if (!selectedService.length) {
        throw new Error('Não foi possível encontrar o serviço do seu agendamento.')
      }

      const formattedServiceList = serviceList.map(
        ({ id, name, additional, category }) => ({
          value: id,
          name,
          category,
          photo: undefined,
          additional
        })
      )

      const {
        additionalServices,
        mainServices
      } = splitServicesList(formattedServiceList)

      const additionalServicesNames = schedulingInfo?.additionalServices?.split(', ')

      const additionalServicesSelected = []

      if (additionalServicesNames.length > 0) {
        additionalServicesNames.forEach(name => {
          const service = additionalServices?.find(({ name: serviceName }) => serviceName === name)

          if (service) {
            additionalServicesSelected.push(service?.value)
          }
        })
      }

      updatedState = {
        ...updatedState,
        activeCampaign: schedulingInfo?.campaign || undefined,
        petsData: petsList,
        serviceData: mainServices,
        additionalServiceData: additionalServices,
        scheduleBody: {
          additional_services: additionalServicesSelected,
          pet_id: selectedPet?.[0]?.id,
          service_id: schedulingInfo?.service?.id
        },
        selectedPet: selectedPet?.[0],
        activeStep: 2
      }

      // step 2

      const partnerSelected = await getPartnerById(schedulingInfo.partnerId)
      if (!partnerSelected?.id) {
        throw new Error('Não foi possível encontrar a loja do seu agendamento.')
      }

      const daySelected = moment(schedulingInfo?.day?.split('-'), 'DD/MM/YY')
      // caso seja a edição de um agendamento é considerado o mês e ano do agendamento
      // caso seja a repetição de um agendamento é considerado o mês e ano atuais
      const currentMonth = isEditing ? daySelected.month() : moment().month()
      const currentYear = isEditing ? daySelected.year() : moment().year()

      const availableSlots = await calculateAvailabilities({
        partnerId: partnerSelected?.id,
        serviceId: schedulingInfo?.service?.id,
        additionalServices: additionalServicesSelected,
        breedId: schedulingInfo?.pet?.breed?.id,
        temperament: schedulingInfo?.pet?.temperament,
        month: currentMonth + 1,
        year: currentYear
      })

      const professionalIds = Object.keys(availableSlots).filter(
        (professionalId) => professionalId !== ''
      )

      const professionalList = await getNames(professionalIds)

      const professionalSelected = professionalList?.find(({ id }) => id === schedulingInfo.professionalId)

      const partnerEnablePayment = partnerSelected?.enabledPayment === true

      const partnerOpenSeller = partnerSelected?.openSeller === true

      const professionalEnablePayment = professionalSelected?.enabledPayment === true

      const havePayment = partnerEnablePayment &&
        (professionalEnablePayment || partnerOpenSeller)

      const professionalData = professionalList
        .map(({ id, name, photo, enabledPayment, ratingAverage }) => ({ id, name, photo, enabledPayment: enabledPayment || false, ratingAverage }))
        .filter(({ enabledPayment }) => (havePayment && enabledPayment === havePayment) || !havePayment)

      if (!professionalData.length) {
        const unavailablePetSchedule = petsList.find(
          (pet) => selectedPet?.[0] && pet.id === selectedPet?.[0]?.id
        )

        if (unavailablePetSchedule) {
          const newError = {
            ...updatedState?.error,
            unavailabilityPets: [
              ...updatedState?.error.unavailabilityPets,
              {
                ...unavailablePetSchedule
              }
            ]
          }
          updatedState.error = newError
        }
      }

      updatedState.professionalData = professionalData

      updatedState = {
        ...updatedState,
        activeStep: 4
      }

      const calendarDate = {
        month: currentMonth + 1,
        year: currentYear
      }

      const services = [...additionalServicesSelected, schedulingInfo?.service?.id]

      const productCoupon = await handleFetchCoupon()

      const [campaignList, price] = await Promise.all([
        // busca por campanhas disponíveis no mês
        handleFetchCampaigns(
          calendarDate,
          partnerSelected?.id,
          schedulingInfo?.service?.id
        ),
        // busca pelo preço dos serviços
        getPrice({
          partnerId: partnerSelected?.id,
          breedId: schedulingInfo?.pet?.breed?.id,
          services
        })
      ])

      const pricesData = price
        ? price.map((priceData) => {
          return {
            service: priceData.service.name,
            price: priceData.price_value,
            service_time: priceData.service_time,
            additional: priceData.service.additional
          }
        })
        : []

      updatedState = {
        ...updatedState,
        scheduleBody: {
          ...updatedState?.scheduleBody,
          partner_id: partnerSelected?.id,
          professional_id: ''
        },
        isCustomAddress: true,
        customAddressType: 'storeName',
        storeName: schedulingInfo.partnerFantasyName,
        totalPartners: 1,
        partnersData: [partnerSelected],
        calendarMonth: currentMonth + 1,
        calendarYear: currentYear,
        timeData: [],
        campaigns: campaignList,
        pricesData,
        availableSlots,
        changeStore: false,
        usingGeolocation: false,
        error: {
          locations: 'Informe sua localização',
          professional_id: 'Selecione um profissional para o atendimento',
          selectDay: 'Selecione um dia para agendamento',
          selectTime: 'Selecione um horário para atendimento',
          unavailabilityPets: []
        }
      }

      if (!professionalSelected?.id) {
        throw new Error('Não foi possível encontrar a profissional do seu agendamento.')
      }

      // se for para a edição re-adiciona o horario marcado do agendamento no quadro de horarios disponiveis
      if (!withoutDate) {
        availableSlots[professionalSelected.id][daySelected.format('D')].push(schedulingInfo?.time)
      }

      const availableDates = Object.keys(availableSlots[professionalSelected.id])?.map((day) => parseInt(day))

      updatedState = {
        ...updatedState,
        availableDates
      }

      // step 3 e 4

      // payload para parar no fluxo de seleção de data/hora
      if (withoutDate) {
        updatedState = {
          ...updatedState,
          scheduleBody: {
            ...updatedState.scheduleBody,
            professional_id: professionalSelected?.id,
            selectDay: '',
            selectTime: ''
          },
          professionalData: professionalList,
          timeData: availableSlots[professionalSelected?.id][daySelected.format('D')],
          times: availableSlots[professionalSelected?.id][daySelected.format('D')],
          invertStep: false,
          obs: schedulingInfo?.obs,
          activeStep: 5,
          review: false,
          isHowToPay: false,
          productCoupon,
          error: {
            selectDay: 'Selecione um dia para agendamento',
            selectTime: 'Selecione um horário para atendimento',
            unavailabilityPets: []
          },
          schedulingData: {
            pet: selectedPet?.[0],
            service: schedulingInfo?.service,
            partner: partnerSelected,
            professional: professionalSelected
          }
        }

        return updatedState
      }

      updatedState = {
        ...updatedState,
        scheduleBody: {
          ...updatedState.scheduleBody,
          professional_id: professionalSelected?.id,
          selectDay: daySelected.format('YYYY-MM-DD'),
          selectTime: schedulingInfo?.time
        },
        professionalData: professionalList,
        timeData: availableSlots[professionalSelected?.id][daySelected.format('D')],
        times: availableSlots[professionalSelected?.id][daySelected.format('D')],
        schedulingCoupon: schedulingInfo?.schedulingCoupon,
        invertStep: false,
        obs: schedulingInfo?.obs,
        activeStep: 6,
        review: true,
        productCoupon,
        error: {
          unavailabilityPets: []
        },
        schedulingData: {
          pet: selectedPet?.[0],
          service: schedulingInfo?.service,
          partner: partnerSelected,
          professional: professionalSelected,
          selectDay: daySelected.format('YYYY-MM-DD'),
          selectTime: schedulingInfo?.time,
          hasOnlinePayment: schedulingInfo.hasOnlinePayment,
          schedulingCoupon: schedulingInfo?.schedulingCoupon
        },
        isEditingScheduling: true
      }

      return updatedState
    } catch (err) {
      return {
        ...updatedState,
        reeschedulingError: true
      }
    }
  }

  const steps = [
    {
      step: 1,
      url: 'pet-e-servico',
      label: 'Pet + serviço',
      disabledRule: !state.scheduleBody.service_id || state.scheduleBody.pet_id === undefined,
      path: '/pet-e-servico',
      id: 1
    },
    {
      step: 2,
      url: 'loja',
      label: 'Local',
      disabledRule: (!state.scheduleBody.partner_id || (state.error.unavailabilityPets.length > 0 && state.scheduleBody.samePetDate) || !state.partnersData.length),
      nextHiddenRule: !state.scheduleBody.partner_id,
      active: state.scheduleBody?.partner_id && !!Object.keys(state?.availableSlots || {}).length,
      path: '/loja',
      id: 2
    },
    {
      step: 3,
      url: 'selecao-de-fluxo',
      label: 'Seleção de fluxo',
      disabledRule: state.invertStep === undefined,
      active: state.invertStep !== undefined,
      path: '/selecao-de-fluxo',
      id: 3
    },
    {
      step: handleShouldInvertSteps() ? 5 : 4,
      url: 'profissional',
      label: 'Profissional',
      disabledRule: (!state.scheduleBody.professional_id && !state.noPreference),
      active: state.scheduleBody?.professional_id || state.noPreference,
      path: '/profissional',
      id: handleShouldInvertSteps() ? 5 : 4
    },
    {
      step: handleShouldInvertSteps() ? 4 : 5,
      url: 'data',
      label: 'Data e horário',
      disabledRule: !state.scheduleBody.selectTime,
      active: state.invertStep ? (state?.updatedProfessionals && state.scheduleBody?.selectTime) : state.scheduleBody?.selectTime,
      path: '/data',
      id: handleShouldInvertSteps() ? 4 : 5
    },
    {
      step: 6,
      url: 'revisao-do-agendamento',
      label: 'Revise seu agendamento',
      disabledRule: (!state.scheduleBody.partner_id || !state.scheduleBody.professional_id || !state.scheduleBody.service_id || state.scheduleBody.pet_id === undefined || !state.scheduleBody.selectDay || !state.scheduleBody.selectTime),
      path: '/revisao-do-agendamento',
      id: 5
    },
    {
      step: 7,
      url: 'como-pagar',
      label: 'Escolha como pagar',
      disabledRule: (!state.scheduleBody.partner_id || !state.scheduleBody.professional_id || !state.scheduleBody.service_id || state.scheduleBody.pet_id === undefined || !state.scheduleBody.selectDay || !state.scheduleBody.selectTime || !state.scheduleBody.paymentType),
      path: '/como-pagar',
      id: 6
    }
  ]

  // retorna estado para o momento de cada step (apenas durante o fluxo por exemplos: tela de resumo e botão voltar)
  const editSteps = {
    0: {
      ...INITIAL_SCHEDULING_STATE,
      serviceData: state.serviceData,
      additionalServiceData: state.additionalServiceData,
      obs: state.obs
    },
    1: {
      ...INITIAL_SCHEDULING_STATE,
      scheduleBody: {
        ...INITIAL_SCHEDULING_STATE.scheduleBody,
        pet_id: state.selectedPet?.id,
        service_id: state.scheduleBody.service_id,
        additional_services: state.scheduleBody.additional_services
      },
      serviceData: state.serviceData,
      additionalServiceData: state.additionalServiceData,
      selectedPet: state.selectedPet,
      formList: state.formList,
      petsData: state.petsData,
      obs: state.obs,
      isAllAdditionalServicesSelected: state.isAllAdditionalServicesSelected,
      activeStep: 1,
      error: {
        ...INITIAL_SCHEDULING_STATE?.error,
        pet_id: undefined,
        unavailabilityPets: [],
        service_id: undefined
      }
    },
    2: {
      ...INITIAL_SCHEDULING_STATE,
      pricesData: state.pricesData ? state.pricesData : [],
      changeStore: state.changeStore,
      storeName: state.storeName,
      usingGeolocation: state.usingGeolocation,
      isCustomAddress: state.isCustomAddress,
      customAddressType: state.customAddressType,
      address: state.address,
      productCoupon: state.productCoupon,
      campaigns: state.campaigns,
      scheduleBody: {
        ...INITIAL_SCHEDULING_STATE.scheduleBody,
        pet_id: state.selectedPet?.id,
        service_id: state.scheduleBody.service_id,
        additional_services: state.scheduleBody?.additional_services || [],
        partner_id: state.scheduleBody.partner_id,
        locations: state.scheduleBody.locations,
        samePetDate: state.scheduleBody?.samePetDate
      },
      serviceData: state.serviceData,
      additionalServiceData: state.additionalServiceData,
      selectedPet: state.selectedPet,
      selectedPetSchedule: state?.selectedPetSchedule,
      petsData: state.petsData,
      formList: state.formList,
      partnersData: state.partnersData,
      availableDates: state.availableDates,
      availableSlots: state.availableSlots,
      activeStep: 2,
      error: {
        ...INITIAL_SCHEDULING_STATE?.error,
        pet_id: undefined,
        unavailabilityPets: [],
        service_id: undefined,
        partner_id: undefined
      },
      obs: state.obs
    },
    3: {
      ...state,
      calendarMonth: state.calendarMonth,
      calendarYear: state.calendarYear,
      productCoupon: state.productCoupon,
      campaigns: state.campaigns,
      scheduleBody: {
        ...state.scheduleBody,
        selectDay: '',
        selectTime: '',
        professional_id: ''
      },
      activeStep: 3,
      obs: state.obs
    },
    4: {
      ...state,
      calendarMonth: state.calendarMonth,
      calendarYear: state.calendarYear,
      productCoupon: state.productCoupon,
      campaigns: state.campaigns,
      scheduleBody: {
        ...state.scheduleBody,
        ...(handleShouldInvertSteps() ? {
          professional_id: ''
        } : {
          selectDay: '',
          selectTime: ''
        })
      },
      activeStep: 4,
      noPreference: false,
      obs: state.obs
    },
    5: {
      ...state,
      activeStep: 5,
      obs: state.obs,
      review: true,
      isHowToPay: false,
      finalStep: true,
      scheduleBody: {
        ...state.scheduleBody,
        paymentType: undefined
      }
    }
  }

  // exporta as funções
  const functions = {
    showSchedulingFailureModal,
    handleSetRescheduleDataLoaded,
    checkReservedDate,
    hasSchedulingChanged,
    handleEditPetScheduling,
    handleShouldInvertSteps,
    handleAddPetToScheduling,
    handleUnselectPet,
    handleConfirmScheduling,
    handleWarningModalClose,
    handleFetchCampaigns,
    handleFetchCoupon,
    pricesInfo,
    findPartnerScheduleAndCampaignAviability,
    handleEditExternalScheduling,
    handleEditMultipleScheduling,
    addCouponToSchedule,
    removeCouponFromSchedule
  }

  // Salva o estado no localStorage sempre que ele for atualizado
  useEffect(() => {
    saveStateToLocalStorage(state)
  }, [state])

  return (
    <serviceScheduleContext.Provider value={{ state, dispatch, functions, steps, editSteps }}>
      <PetAndServicesContextProvider>
        <LocationContextProvider>
          <ProfessionalContextProvider>
            <DateTimeContextProvider>
              {children}
            </DateTimeContextProvider>
          </ProfessionalContextProvider>
        </LocationContextProvider>
      </PetAndServicesContextProvider>
    </serviceScheduleContext.Provider>
  )
}

// Hook para acessar o contexto
export function useServiceScheduleContext () {
  const context = useContext(serviceScheduleContext)
  if (!context) {
    throw new Error('useServiceScheduleContext deve ser usado dentro de um Provider!')
  }
  const { state, dispatch, functions, steps, editSteps } = context
  return { state, dispatch, functions, steps, editSteps }
}
